import { Box, Paper } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import { useGetUsersMinFilteredQuery } from '@api/user';
import {
  Piece,
  RecipientAutoDetection,
  RecipientAutoDetectionStatus,
  User,
  UserListParams,
  UserMin,
} from '@apiContract';
import { useAssignmentRecipient } from '@components/Capture/hooks/useAssignmentRecipient';
import { AddEvent } from '@components/Capture/hooks/useUpdateQueue';
import Search from '@uiKit/SearchInput';

import Footer from '../Footer';
import Title from '../Title';
import AssignTO from './components/AssignTo';
import List from './components/List';
import UserForm from './components/UserForm';
import { sxP20, sxPaper, sxTab, sxTabsSection } from './styles';

const ITEMS_PER_PAGE = 20;

const initialFilters = {
  UserRole: 'Recipient',
  ItemsPerPage: ITEMS_PER_PAGE,
  PageNumber: 0,
  SearchText: '',
  UserStatus: 1,
};

type UserListData = {
  piece?: Piece;
  pieceDetection?: RecipientAutoDetection;
  error: unknown;
  loading: boolean;
};

type UserListProps = {
  darkMode?: boolean;
  file: UserListData | null;
  onClose: () => void;
  addEvent: (param: AddEvent) => void;
  pieces?: Piece[];
};

const getBulkCardDisplay = (pieces: Piece[]): { user: Piece['recipient']; unassignedText: string } => {
  if (!pieces.length) return { user: undefined, unassignedText: 'Unassigned' };

  const firstRecipient = pieces[0].recipient;
  const allSame = pieces.every((p) => p.recipient?.id === firstRecipient?.id);

  if (allSame) return { user: firstRecipient, unassignedText: 'Unassigned' };

  return { user: undefined, unassignedText: 'Multiple' };
};

const AssignmentList = ({ file, onClose, addEvent, darkMode, pieces: bulkPieces }: UserListProps) => {
  const isBulk = !!bulkPieces?.length;
  const { pieceDetection, piece, error, loading } = file || {};
  const autoDetection = pieceDetection || piece?.recipientAutoDetection;
  const bulkCardDisplay = isBulk ? getBulkCardDisplay(bulkPieces) : null;
  const [isAutoDetectionApplied, setIsAutoDetectionApplied] = useState(!!autoDetection && !isBulk);
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedUser, setSelectedUser] = useState<User | UserMin | undefined | null>(
    isBulk ? bulkCardDisplay?.user : piece?.recipient || (isAutoDetectionApplied && autoDetection?.recipient) || null,
  );
  const [filters, setFilters] = useState<UserListParams>({
    ...initialFilters,
    SearchText: (isAutoDetectionApplied && !selectedUser && autoDetection?.recipientSearchString) || '',
  });
  const [items, setItems] = useState<Array<UserMin>>([]);
  const { data, isFetching } = useGetUsersMinFilteredQuery(filters);
  const { assignRecipientToPiece } = useAssignmentRecipient(addEvent);

  const loadNextPage = useCallback((startIndex: number) => {
    setFilters((prev) => ({
      ...prev,
      PageNumber: Math.floor(startIndex / ITEMS_PER_PAGE),
      ItemsPerPage: ITEMS_PER_PAGE,
    }));
  }, []);

  const selectUser = (user: UserMin) => {
    setSelectedUser(user);
  };

  useEffect(() => {
    if (data?.pageNumber === 0) {
      setItems(data.items || []);
    } else {
      setItems((prev) => [...prev, ...(data?.items || [])]);
    }
  }, [data]);

  useEffect(() => {
    if (
      !selectedUser &&
      autoDetection?.recipient &&
      autoDetection?.status === RecipientAutoDetectionStatus.IdentifiedVerificationRequired
    ) {
      setSelectedUser(autoDetection.recipient);
    }
  }, [selectedUser, autoDetection?.recipient, autoDetection?.status]);

  const onSearch = (value: string) => {
    setFilters({ ...initialFilters, SearchText: value, PageNumber: 0 });
  };

  const changeTab = () => setTabIndex((prev) => (!prev ? 1 : 0));

  const hasNextPage = (data?.totalCount || 0) > items.length;

  const addUserToPiece = (user?: User) => {
    const currentUser = user || selectedUser;

    if (isBulk && bulkPieces && currentUser) {
      bulkPieces.forEach((p) => assignRecipientToPiece(currentUser, p));
    } else if (piece && currentUser) {
      assignRecipientToPiece(currentUser, piece);
    }

    if (user) {
      setSelectedUser(user);
    }
  };

  const handleSubmit = () => {
    addUserToPiece();
    onClose();
  };

  const applyAutoDetection = () => {
    setIsAutoDetectionApplied(true);
    if (autoDetection?.recipient) {
      setSelectedUser(autoDetection?.recipient);
    }

    if (autoDetection?.recipientSearchString) {
      onSearch(autoDetection?.recipientSearchString);
    }
  };

  const isConfirm =
    !isBulk &&
    !!selectedUser &&
    selectedUser.id === autoDetection?.recipient?.id &&
    !piece?.recipient &&
    [
      RecipientAutoDetectionStatus.Identified,
      RecipientAutoDetectionStatus.IdentifiedBasedOnPreviousAssignment,
      RecipientAutoDetectionStatus.IdentifiedVerificationRequired,
    ].includes(autoDetection?.status);

  const footerDisabled = isBulk
    ? !selectedUser?.id || (!!bulkCardDisplay?.user && selectedUser?.id === bulkCardDisplay.user?.id)
    : !selectedUser?.id || selectedUser?.id === piece?.recipient?.id;

  return (
    <Paper elevation={3} sx={sxPaper}>
      <Title title="Assign" onClose={onClose} isLoading={!isBulk && (loading || !piece?.recipientAutoDetection)} />
      <Box component="section" sx={sxTabsSection(tabIndex)}>
        <Box display="flex" sx={sxTab}>
          <AssignTO
            user={selectedUser}
            recipientDetection={isBulk ? undefined : autoDetection}
            applyAutoDetection={isBulk ? undefined : applyAutoDetection}
            piece={isBulk ? undefined : piece}
            unassignedText={bulkCardDisplay?.unassignedText}
            showAlert={
              !isBulk &&
              !error &&
              !!autoDetection &&
              (!piece?.recipient ||
                (autoDetection?.recipientId === piece?.recipient?.id &&
                  autoDetection?.status !== RecipientAutoDetectionStatus.IdentifiedVerificationRequired))
            }
          />
          <Search setSearchResult={onSearch} sx={sxP20} defaultSearch={filters.SearchText} isLoading={isFetching} />
          <List
            darkMode={darkMode}
            changeTab={changeTab}
            loadNextPage={loadNextPage}
            isNextPageLoading={isFetching}
            items={items}
            setSelectedUser={selectUser}
            selectedUser={selectedUser}
            hasNextPage={hasNextPage}
          />
          <Footer
            sx={sxP20}
            disabled={footerDisabled}
            title={isConfirm ? 'Confirm Recipient' : 'Save Changes'}
            handleSubmit={handleSubmit}
          />
        </Box>
        <UserForm changeTab={changeTab} assignUser={addUserToPiece} defaultSearch={filters.SearchText} />
      </Box>
    </Paper>
  );
};

export default AssignmentList;
